import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef, OnInit, OnChanges } from '@angular/core';

export interface ComboBoxItem {
  id: string;
  name: string;
}

@Component({
  selector: 'no-kno-combo-box-select',
  templateUrl: './combo-box-select.component.html',
  styleUrls: ['./combo-box-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ComboBoxSelectComponent implements OnInit, OnChanges {
  
  // Constants
  private readonly FILTER_THRESHOLD = 10;
  private readonly VISIBLE_ITEMS_COUNT = 6; // Number of items visible in dropdown
  
  // Inputs
  @Input() items: ComboBoxItem[] = [];
  @Input() selectedValue: string = '';
  @Input() placeholder: string = 'Type or select from list';
  @Input() readonly: boolean = false;
  @Input() loading: boolean = false;
  
  // Outputs
  @Output() valueChange = new EventEmitter<string>();
  @Output() itemSelected = new EventEmitter<ComboBoxItem>();
  
  // Internal state
  isDropdownOpen = false;
  isInputFocused = false;
  filteredItems: ComboBoxItem[] = [];
  searchValue: string = '';
  private previousValue: string = '';
  
  constructor(private cdr: ChangeDetectorRef) {}
  
  ngOnInit(): void {
    this.previousValue = this.selectedValue;
    this.updateFilteredItems();
  }
  
  ngOnChanges(): void {
    // Only emit if the value actually changed and it's not a programmatic change
    if (this.selectedValue !== this.previousValue) {
      this.previousValue = this.selectedValue;
    }
    this.updateFilteredItems();
  }
  
  // Computed properties
  get shouldShowFilter(): boolean {
    return this.items.length > this.FILTER_THRESHOLD;
  }

  get dropdownMaxHeight(): string {
    // Each dropdown option is approximately 44px height (12px padding top/bottom + text height)
    // Search box adds about 52px when visible (8px container padding + 36px input height)
    const itemHeight = 44;
    const searchBoxHeight = this.shouldShowFilter ? 52 : 0;
    const maxItemsHeight = this.VISIBLE_ITEMS_COUNT * itemHeight;
    const totalHeight = maxItemsHeight + searchBoxHeight;
    return `${totalHeight}px`;
  }

  get listAreaMaxHeight(): string {
    // Height for just the scrollable list area (excluding fixed search box)
    const itemHeight = 44;
    const maxItemsHeight = this.VISIBLE_ITEMS_COUNT * itemHeight;
    return `${maxItemsHeight}px`;
  }
  
  get selectedItem(): ComboBoxItem | undefined {
    return this.items.find(item => item.id === this.getSelectedId());
  }
  
  private getSelectedId(): string {
    // Find item by name to get the id
    const item = this.items.find(item => item.name === this.selectedValue);
    return item?.id || '';
  }
  
  // Event handlers
  onInputFocus(): void {
    this.isInputFocused = true;
  }
  
  onInputBlur(): void {
    this.isInputFocused = false;
  }
  
  onInputChange(): void {
    // Only emit valueChange if the user is actively typing (input is focused)
    // This prevents emissions when the value is changed programmatically from the parent
    if (this.isInputFocused && this.selectedValue !== this.previousValue) {
      this.previousValue = this.selectedValue;
      this.valueChange.emit(this.selectedValue);
    }
    // Don't filter based on main input anymore - only use search box for filtering
    // this.updateFilteredItems();
  }

  onSearchChange(): void {
    // Handle search box input changes
    this.updateFilteredItems();
  }
  
  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      // Close dropdown on enter
      this.isDropdownOpen = false;
      this.cdr.markForCheck();
    }
  }
  
  toggleDropdown(): void {
    this.isDropdownOpen = !this.isDropdownOpen;
    if (this.isDropdownOpen) {
      // Clear search when opening dropdown
      this.searchValue = '';
    }
    this.updateFilteredItems();
    this.cdr.markForCheck();
  }
  
  selectItem(item: ComboBoxItem): void {
    this.selectedValue = item.name;
    this.previousValue = item.name;
    this.isDropdownOpen = false;
    this.valueChange.emit(this.selectedValue);
    this.itemSelected.emit(item);
    this.cdr.markForCheck();
  }
  
  isItemSelected(item: ComboBoxItem): boolean {
    const selectedId = this.getSelectedId();
    return selectedId === item.id;
  }
  
  private updateFilteredItems(): void {
    // Use search box value for filtering when it exists and items > threshold
    if (this.shouldShowFilter && this.searchValue.trim()) {
      const filterValue = this.searchValue.toLowerCase().trim();
      this.filteredItems = this.items.filter(item => 
        item.name.toLowerCase().includes(filterValue)
      );
    } else {
      // Show all items when no search or items <= threshold
      this.filteredItems = [...this.items];
    }
  }
}
